import { groq } from "next-sanity";
import { handleClientError } from "../../errors";
import { getClient } from "../sanity";
import { type Customer, customerFragment } from "./customers";

export type CaseStudyIndustry = {
  name: string;
  slug: "b2b" | "fintech" | "media" | "retail";
};

export type CaseStudy = CaseStudySimple & {
  content: string;
  metaTitle?: string;
  metaDescription?: string;
  metaImageUrl?: string;
};

export const getCaseStudy = async (slug: string): Promise<CaseStudy | undefined> => {
  const query = groq`
    *[_type == "caseStudy" && customer->slug == $slug] {
      content,
      metaTitle,
      metaDescription,
      'metaImageUrl': metaImage.asset->url,
      ${caseStudySimpleFragment}
    }
  `;

  try {
    const parameters = { slug };
    const caseStudies = await getClient().fetch(query, parameters);
    return caseStudies[0];
  } catch (error) {
    const message = `Failed to fetch case study with slug "${slug}": ${
      error instanceof Error ? error.message : String(error)
    }`;
    handleClientError(message);
    return undefined;
  }
};

export type CaseStudySimple = {
  title: string;
  industry: CaseStudyIndustry;
  customer: Customer;
  fullImageUrl: string;
  croppedImageUrl: string;
  _updatedAt: string;
};

const caseStudySimpleFragment = `
    title,
    industry->{ name, 'slug': slug.current },
    customer->{ ${customerFragment} },
    'fullImageUrl': fullImage.asset->url,
    'croppedImageUrl': croppedImage.asset->url,
    _updatedAt,
  `;

export const getSimpleCaseStudies = async (slugs?: string[]): Promise<CaseStudySimple[]> => {
  const baseQuery = groq`*[_type == "caseStudy" && !hidden`;
  const slugFilter = slugs && slugs.length > 0 ? groq` && customer->slug in $slugs` : "";
  const endQuery = groq`] {
    ${caseStudySimpleFragment}
  } | order(_updatedAt desc)`;

  try {
    const query = baseQuery + slugFilter + endQuery;
    const parameters = { slugs: slugs ?? [] };
    const caseStudies = await getClient().fetch<CaseStudySimple[]>(query, parameters);
    return caseStudies;
  } catch (error) {
    const message = `Failed to fetch simple case studies${slugs ? ` with slugs "${slugs.join(", ")}"` : ""}: ${
      error instanceof Error ? error.message : String(error)
    }`;
    handleClientError(message);
    return [];
  }
};
